import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Card, CardBody, Container, Spinner } from 'reactstrap';
import { useHistory } from 'react-router-dom';

import * as aiAgentsActions from '../../store/aiAgents/actions';
import * as leadFieldsActions from '../../store/leadFields/actions';
import { useDispatch, useSelector } from 'react-redux';
import FormAiAgent from '../../components/AiAgents/FormAiAgent';
import Modal from '../../components/Common/Modal/Modal';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DataTableNext from '../../components/Common/DataTable/DataTableNext';
import createTableColumns from '../../components/AiAgents/tableColumns';
import {
  CreateButton,
  ToggleColumnButton,
} from '../../components/Common/DataTable/tableActionButtons';
import { addOrRemove } from '../../helpers/sharedFunction';
import { useTranslation } from 'react-i18next';

const List = () => {
  const loading = useSelector((state) => state.AiAgent.loading);
  const aiAgents = useSelector((state) => state.AiAgent.aiAgents);

  const [editModal, setEditModal] = useState(false);
  const [selectedAiAgent, setSelectedAiAgent] = useState(null);

  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    dispatch(aiAgentsActions.syncAiAgents());
    dispatch(leadFieldsActions.syncLeadFields());
    // We own the aiAgents domain (this page is its only consumer), so
    // resetting it on unmount cleanly stops its Firestore listener with
    // no side effects elsewhere.
    // We deliberately do NOT reset leadFields here: it is shared state
    // consumed by several other pages/forms, and RESET_LEAD_FIELD_STATE
    // wipes the whole collection back to initialState rather than just
    // cancelling this page's listener. Its saga watches SYNC_LEAD_FIELDS
    // with takeLatest, so the stale listener started above is already
    // cancelled the next time any consumer re-syncs leadFields.
    return () => {
      dispatch(aiAgentsActions.resetState());
    };
  }, []);

  const openEditModal = (aiAgent = null) => {
    setSelectedAiAgent(aiAgent);
    setEditModal(true);
  };

  const openSimulator = useCallback(
    (aiAgent) => history.push(`/aiAgents/simulate/${aiAgent.id}`),
    [history],
  );

  const columns = useMemo(
    () => createTableColumns(openEditModal, openSimulator),
    [openSimulator],
  );
  const [hiddenColumns, setHiddenColumns] = useState(
    columns.filter(({ hidden }) => !!hidden).map(({ name }) => name),
  );
  const handleToggleColumn = (name) => {
    setHiddenColumns(addOrRemove(hiddenColumns, name));
  };

  const rows = useMemo(
    () =>
      aiAgents.map((aiAgent) => ({
        ...aiAgent,
        collectFieldsCount: Array.isArray(aiAgent.collectFields)
          ? aiAgent.collectFields.length
          : 0,
      })),
    [aiAgents],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title={t('AI Agents')} />
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Row className="align-items-center justify-content-end mb-2">
                    <Spinner
                      color="secondary"
                      animation="border"
                      hidden={!loading}
                    />
                    <CreateButton
                      label="Create new AI Agent"
                      disabled={loading}
                      onPress={openEditModal}
                    />
                    <ToggleColumnButton
                      columns={columns}
                      hiddenColumns={hiddenColumns}
                      onToggleColumn={handleToggleColumn}
                    />
                  </Row>
                  <DataTableNext
                    loading={loading}
                    rows={rows}
                    columns={columns}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    currentPage={currentPage}
                    onCurrentPageChange={setCurrentPage}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Modal
        size="xl"
        title="AI Agent"
        isOpen={editModal}
        toggle={() => setEditModal(!editModal)}
        scrollable={false}
      >
        <FormAiAgent
          aiAgent={selectedAiAgent}
          create={!selectedAiAgent}
          loading={loading}
          closeModal={() => setEditModal(false)}
        />
      </Modal>
    </React.Fragment>
  );
};

export default List;
